import { ExternalLink, RefreshCw } from "lucide-react";
import {
  LGoBadge,
  LGoButton,
  LGoCard,
  LGoCardHeader,
  LGoCardTitle,
} from "@/ui/branded";
import {
  canRequestOnboardingLink,
  onboardingStage,
  outstandingRequirements,
  unavailableCapabilities,
} from "@/domain/payment-account/rules";
import type { AccountRequirement, PaymentAccount } from "@/domain/payment-account/types";
import { PaymentAccountDiagnostics } from "./PaymentAccountDiagnostics";
import { capabilityLabels, rememberedCopy, stageCopy } from "./payment-account-copy";

export interface PaymentAccountSectionProps {
  account: PaymentAccount;
  isStripeHandoffUnderway: boolean;
  onStartOnboarding: () => void;
  onCreateAccount: () => void;
  onRefresh: () => void;
}

/** `requirement.description` is a dotted machine path, so a requirement Stripe
 *  has not yet rejected has nothing human to say — it says it generically. */
function requirementText(requirement: AccountRequirement): string {
  const explained = requirement.errors.find((error) => error.description);
  return explained?.description ?? "Stripe potřebuje doplnit další údaje — vypíše je ve formuláři.";
}

export function PaymentAccountSection({
  account,
  isStripeHandoffUnderway,
  onStartOnboarding,
  onCreateAccount,
  onRefresh,
}: PaymentAccountSectionProps) {
  const stage = onboardingStage(account);
  const lastReadFailed = account.syncError !== null;
  const copy =
    (lastReadFailed ? rememberedCopy(stage, account.syncedAt) : null) ?? stageCopy[stage];
  const outstanding = [...new Set(outstandingRequirements(account).map(requirementText))];
  const missing = unavailableCapabilities(account);
  // Creation belongs to `no-account` alone: once an account exists it is never
  // replaced from here, because a payout target editable through a shared
  // access code is redirected revenue (lastgo-api ADR 0004).
  const offersAccountCreation = stage === "no-account";
  // An account Stripe cannot even be asked about, or one missing a capability
  // no form can add, gets no button — a link that changes nothing reads as a
  // promise that it might.
  const offersOnboarding =
    canRequestOnboardingLink(account) &&
    (stage === "action-needed" || stage === "verifying" || stage === "restricted");

  return (
    <div className="space-y-4">
      <LGoCard variant="raised">
        <LGoCardHeader>
          <div className="flex items-center gap-3">
            <LGoCardTitle>Platby a výplaty</LGoCardTitle>
            <LGoBadge variant={copy.badgeVariant}>{copy.badge}</LGoBadge>
          </div>
        </LGoCardHeader>

        <h3 className="font-display text-xl font-semibold tracking-tight">{copy.title}</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-prose">{copy.body}</p>

        {stage === "capability-unavailable" && (
          <ul className="mt-3 space-y-1 text-sm text-foreground">
            {missing.map((name) => (
              <li key={name}>{capabilityLabels[name]}</li>
            ))}
          </ul>
        )}

        {outstanding.length > 0 && (
          <div className="mt-4">
            <p className="text-sm text-muted-foreground">Stripe od vás čeká:</p>
            <ul className="mt-1 space-y-1">
              {outstanding.map((text) => (
                <li key={text} className="text-sm text-foreground break-words">
                  {text}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          {offersAccountCreation && (
            <LGoButton
              onClick={onCreateAccount}
              loading={isStripeHandoffUnderway}
              variant="primary"
              iconLeft={<ExternalLink />}
            >
              Zřídit účet a spustit ověření
            </LGoButton>
          )}
          {offersOnboarding && (
            <LGoButton
              onClick={onStartOnboarding}
              loading={isStripeHandoffUnderway}
              variant={stage === "action-needed" ? "primary" : "secondary"}
              iconLeft={<ExternalLink />}
            >
              {stage === "action-needed" ? "Pokračovat v ověření" : "Otevřít ověření ve Stripe"}
            </LGoButton>
          )}
          <LGoButton variant="ghost" onClick={onRefresh} iconLeft={<RefreshCw />}>
            Načíst stav znovu
          </LGoButton>
        </div>
      </LGoCard>

      <PaymentAccountDiagnostics account={account} />
    </div>
  );
}
